/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  GetGcpSetupInfoRequest,
  GetGcpSetupInfoSecurity,
} from "../models/operations/getgcpsetupinfo.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildGcpSetupInfoQuery,
  GcpSetupInfoQueryData,
  prefetchGcpSetupInfo,
  queryKeyGcpSetupInfo,
} from "./gcpSetupInfo.core.js";
export {
  buildGcpSetupInfoQuery,
  type GcpSetupInfoQueryData,
  prefetchGcpSetupInfo,
  queryKeyGcpSetupInfo,
};

export type GcpSetupInfoQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * getGcpSetupInfo externalCredentials
 *
 * @remarks
 * Report what the customer must grant in their own GCP project before Speakeasy can impersonate a service account there. Readable before any credential exists, since impersonation is a precondition of creating one. Requires org:read.
 */
export function useGcpSetupInfo(
  request?: GetGcpSetupInfoRequest | undefined,
  security?: GetGcpSetupInfoSecurity | undefined,
  options?: QueryHookOptions<GcpSetupInfoQueryData, GcpSetupInfoQueryError>,
): UseQueryResult<GcpSetupInfoQueryData, GcpSetupInfoQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildGcpSetupInfoQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * getGcpSetupInfo externalCredentials
 *
 * @remarks
 * Report what the customer must grant in their own GCP project before Speakeasy can impersonate a service account there. Readable before any credential exists, since impersonation is a precondition of creating one. Requires org:read.
 */
export function useGcpSetupInfoSuspense(
  request?: GetGcpSetupInfoRequest | undefined,
  security?: GetGcpSetupInfoSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    GcpSetupInfoQueryData,
    GcpSetupInfoQueryError
  >,
): UseSuspenseQueryResult<GcpSetupInfoQueryData, GcpSetupInfoQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildGcpSetupInfoQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setGcpSetupInfoData(
  client: QueryClient,
  queryKeyBase: [parameters: { gramSession?: string | undefined }],
  data: GcpSetupInfoQueryData,
): GcpSetupInfoQueryData | undefined {
  const key = queryKeyGcpSetupInfo(...queryKeyBase);

  return client.setQueryData<GcpSetupInfoQueryData>(key, data);
}

export function invalidateGcpSetupInfo(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: { gramSession?: string | undefined }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: [
      "@gram/client",
      "externalCredentials",
      "getGcpSetupInfo",
      ...queryKeyBase,
    ],
  });
}

export function invalidateAllGcpSetupInfo(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalCredentials", "getGcpSetupInfo"],
  });
}
